import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import axios from "axios";
import { Newspaper, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const News = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/news`);
        if (!cancel) setItems(r.data?.items || []);
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const display = items.length ? [...items, ...items] : [];

  return (
    <section
      id="news"
      data-testid="news-section"
      className="relative py-24 md:py-32 overflow-hidden bg-[#FAF7F0]"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 mb-10 md:mb-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <div className="font-eyebrow text-2xl md:text-[30px] text-[#0F3D2E]/85 mb-6 flex items-center gap-3">
              <span className="w-10 h-px bg-[#0F3D2E]/50" />
              — Güncel Bilgiler
            </div>
            <h2 className="font-serif-editorial text-[#0F3D2E] leading-[0.98] text-[8vw] sm:text-[6.4vw] md:text-[4.4vw] lg:text-[3.4vw]">
              Gündemdeki <em className="italic text-[#2E8B4A]">pestisit haberleri.</em>
            </h2>
          </div>
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#0F3D2E]/60">
            <Newspaper size={14} strokeWidth={1.6} />
            Canlı · Google Haberler
          </div>
        </motion.div>
      </div>

      {loading && (
        <div className="text-center text-[13px] tracking-[0.2em] uppercase text-[#0F3D2E]/60 py-16">
          Yükleniyor…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center text-[13px] tracking-[0.2em] uppercase text-[#0F3D2E]/60 py-16">
          Şu an haber alınamadı.
        </div>
      )}

      {!loading && items.length > 0 && (
        <Marquee gradient={false} speed={30} pauseOnHover={true}>
          {display.map((n, i) => (
            <a
              key={i}
              href={n.link}
              target="_blank"
              rel="noopener noreferrer"
              className="mx-3 md:mx-4 w-[300px] md:w-[380px] bg-white rounded-2xl border border-[#E5DFCE] p-6 md:p-8 shadow-sm flex flex-col justify-between h-[260px] md:h-[280px] hover:shadow-[0_20px_50px_-20px_rgba(15,61,46,0.35)] hover:border-[#2E8B4A]/50 transition-all cursor-pointer group"
              data-testid={`news-item-${i}`}
            >
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#2E8B4A] mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2E8B4A]" />
                  Gündem
                </div>
                <h3 className="font-serif-editorial font-bold text-[#0F3D2E] text-lg md:text-xl leading-snug mb-3 line-clamp-3 group-hover:text-[#2E8B4A]">
                  {n.title}
                </h3>
                <p className="font-ubuntu text-[13px] leading-relaxed text-[#0A1F17]/80 line-clamp-4">
                  {n.summary}
                </p>
              </div>
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#E5DFCE]">
                <span className="text-[10px] tracking-[0.18em] uppercase text-[#0A1F17]/60">{n.source}</span>
                <ExternalLink size={13} strokeWidth={1.5} className="text-[#0F3D2E]/60 group-hover:text-[#2E8B4A]" />
              </div>
            </a>
          ))}
        </Marquee>
      )}
    </section>
  );
};

export default News;
